import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { PaymentsTab } from "@/components/dashboard/PaymentsTab";
import AutoInvoicingTab from "@/components/invoicing/AutoInvoicingTab";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, FileText } from "lucide-react";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";

const BillingPaymentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const { language } = useLanguage();
  
  const isAutoInvoicing = location.pathname.includes('auto-invoicing');
  const [activeTab, setActiveTab] = useState(isAutoInvoicing ? 'auto-invoicing' : 'payments');
  
  useEffect(() => {
    setActiveTab(isAutoInvoicing ? 'auto-invoicing' : 'payments');
  }, [isAutoInvoicing]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'payments') {
      navigate('/billing/payments');
    } else {
      navigate('/billing/auto-invoicing');
    }
  };
  
  const pageTitle = language === 'es' ? 'Facturación y Pagos' : 'Billing & Payments';
  
  return (
    <AppShell pageTitle={pageTitle}>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.billing}
        title="Billing & Payments"
        subtitle="Manage invoices, payments, and financial records"
        altText="Daycare billing and payments"
      />
      
      <div className="relative z-10 p-4 md:p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-gray-100 p-1 rounded-xl">
            <TabsTrigger 
              value="payments" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2 flex items-center gap-2"
              data-testid="payments-tab"
            >
              <CreditCard className="w-4 h-4" />
              {language === 'es' ? 'Pagos' : 'Payments'}
            </TabsTrigger>
            <TabsTrigger 
              value="auto-invoicing" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2 flex items-center gap-2"
              data-testid="auto-invoicing-tab"
            >
              <FileText className="w-4 h-4" />
              {language === 'es' ? 'Facturación Automática' : 'Auto Invoicing'}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="payments" className="mt-6">
            <PaymentsTab />
          </TabsContent>
          
          <TabsContent value="auto-invoicing" className="mt-6">
            <AutoInvoicingTab user={user} organization={organization} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
};

export default BillingPaymentsPage;
